import { Router, watchBusy } from "@frui.ts/screens";
import type { interfaces } from "inversify";
import { Modules } from "config";
import { action, computed, observable } from "mobx";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import AppRouter from "services/router";
import PermissionAreaRepository from "repositories/permissionAreaRepository";
import type FaultyCombination from "entities/faultyCombination";
import PermissionArea from "entities/permissionArea";
import SecurityService from "services/securityService";
import EditableDetailViewModel from "../../../editableDetailViewModel";
import FaultyCombinationsViewModel from "./faultyCombinationsViewModel";

@Router.registerRoute({ route: ":permissionAreaId", name: Router.Self })
export default class FaultyCombinationDetailViewModel extends EditableDetailViewModel<FaultyCombination> {
  readonly modelName = "permissionArea";

  @observable permissionAreas: PermissionArea[] = [];
  @observable permissionArea: PermissionArea;

  constructor(
    protected itemId: string,
    protected repository: PermissionAreaRepository,
    protected localizationService: LocalizationService,
    protected appRouter: AppRouter,
    protected notificationService: NotificationService,
    protected securityService: SecurityService,
    protected eventBus: EventBus
  ) {
    super(itemId);
  }

  async onInitialize(): Promise<void> {
    this.permissionAreas = (await this.repository.getAll({ limit: 100, offset: 0 })).items;
    super.onInitialize();
  }

  @watchBusy
  protected async loadDetail() {
    const [faultyCombination, permissionArea] = await Promise.all([
      this.repository.getFaultyCombinations(this.itemId),
      this.repository.get(this.itemId),
    ]);
    this.permissionArea = permissionArea;

    return faultyCombination;
  }

  @computed
  get areaOptions() {
    return this.permissionAreas
      .filter(area => area.id !== this.itemId && !(this.item.faultyCombinations || []).find(item => item.areaId === area.id))
      .map(area => ({ id: area.id, label: area.name }));
  }

  @computed
  get area() {
    return undefined;
  }

  set area(areaId: string | undefined) {
    if (areaId) {
      this.item.faultyCombinations.push({ areaId });
    }
  }

  @computed
  get combinations(): PermissionArea[] {
    return (this.item.faultyCombinations || [])
      .map(combination => this.permissionAreas.find(area => area.id === combination.areaId))
      .filter(item => item !== undefined) as PermissionArea[];
  }

  @action.bound
  removeCombination(areaId: string) {
    this.item.faultyCombinations = this.item.faultyCombinations.filter(item => item.areaId !== areaId);
  }

  backLink() {
    return this.appRouter.getUrl(FaultyCombinationsViewModel);
  }

  updateData() {
    return this.repository.updateFaultyCombinations(this.itemId, this.item);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.users, `faultyCombinations.${code}`, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) => {
      return new FaultyCombinationDetailViewModel(
        itemId,
        container.get(PermissionAreaRepository),
        container.get(LocalizationService),
        container.get(AppRouter),
        container.get(NotificationService),
        container.get(SecurityService),
        container.get(EventBus)
      );
    };
  }
}
